/**
 * CHỤP KHUÔN MẶT — bắt buộc khi chấm công (văn phòng lẫn công trình).
 *
 * HAI CHẾ ĐỘ KHUNG HÌNH:
 *
 *   'chan_dung'  (văn phòng, đăng ký khuôn mặt)
 *       Cắt vuông giữa khung, cạnh 720px. Chỉ cần khuôn mặt, hậu cảnh là
 *       nhiễu — bỏ đi cho ảnh nhẹ và nhận diện chính xác hơn.
 *
 *   'toan_khung' (công trình)
 *       GIỮ NGUYÊN khung hình, cạnh dài 1080px. Nhân viên đứng lùi ra để
 *       khuôn mặt VÀ công trình phía sau cùng nằm trong một tấm ảnh — đó là
 *       bằng chứng "người này có mặt tại chỗ này", thứ thay cho toạ độ GPS ở
 *       nơi máy không bắt được vệ tinh (hầm, nhà xưởng).
 *
 *       Vì sao 1080 chứ không 720: lùi ra lấy được bối cảnh thì khuôn mặt chỉ
 *       còn chiếm một phần nhỏ khung. Ở 720px mặt dễ tụt xuống dưới
 *       FACE_MIN_SIZE_PX = 90 và dịch vụ nhận diện từ chối ảnh (docs/07 §7.4).
 *
 *       Vì sao MỘT ảnh chứ không phải hai (selfie + ảnh cảnh): hai tấm rời
 *       không chứng minh được chúng chụp cùng lúc cùng chỗ. Một khung có cả
 *       hai thì có.
 *
 * Ràng buộc giữ nguyên ở cả hai chế độ:
 *  · CHỈ camera trước (`facingMode: 'user'`). Không cho chọn ảnh từ thư viện —
 *    nếu cho, nhân viên gửi được ảnh cũ hoặc ảnh của người khác.
 *  · Không hiện nút "chọn từ thư viện", không dùng <input type=file accept>
 *    vì trên Android nó mở luôn trình quản lý tệp.
 *  · Ảnh nén WebP, xoá EXIF ngay trên máy (vẽ lại qua canvas).
 *    Server vẫn đóng dấu lại giờ và toạ độ — không tin metadata từ máy.
 *
 * LƯU Ý VỀ QUYỀN RIÊNG TƯ:
 *  Màn hình này nói rõ với nhân viên rằng ảnh dùng để xác minh danh tính và
 *  lưu bao lâu. Nghị định 13/2023/NĐ-CP xếp dữ liệu khuôn mặt vào nhóm NHẠY
 *  CẢM — phải thông báo rõ trước khi thu thập, không được lặng lẽ chụp.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { chanDoanLoi, kiemTraTruoc, type ChanDoanCamera } from '../lib/camera'
import { nenAnh } from '../lib/thietbi'

type TrangThai =
  | 'dang-mo-camera' | 'san-sang' | 'dang-chup'
  | 'loi-quyen' | 'khong-co-camera'
  // Tách riêng khỏi 'khong-co-camera': đây là lỗi CẤU HÌNH TRIỂN KHAI, không
  // phải lỗi máy nhân viên, và cách xử lý hoàn toàn khác.
  | 'khong-bao-mat'

export type CheDoKhung = 'chan_dung' | 'toan_khung'

/** Cạnh dài tối đa của ảnh gửi lên, theo chế độ khung. */
const CANH_ANH: Record<CheDoKhung, number> = { chan_dung: 720, toan_khung: 1080 }

export default function ChupKhuonMat({
  onXong, onHuy,
  tieuDe = 'Xác minh khuôn mặt',
  huongDan = 'Đưa khuôn mặt vào giữa vòng tròn. Bỏ khẩu trang và kính râm.',
  cheDo = 'chan_dung',
  duoiKhung,
  onBoQuaAnh,
}: {
  onXong: (anh: Blob) => void
  onHuy: () => void
  /** Dùng lại cho màn đăng ký: "Đăng ký khuôn mặt — ảnh 2/3" */
  tieuDe?: string
  huongDan?: string
  cheDo?: CheDoKhung
  /** Ô chọn công trình / ghi chú công việc, hiện ngay dưới khung ngắm */
  duoiKhung?: ReactNode
  /**
   * Camera hỏng hoặc không được cấp quyền mà vẫn cho chấm công.
   * Chỉ truyền vào khi mạng công ty đã xác nhận có mặt — server sẽ đưa bản
   * ghi vào chờ duyệt chứ không từ chối (`face_policy`, cổng hai lớp).
   */
  onBoQuaAnh?: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [trangThai, setTrangThai] = useState<TrangThai>('dang-mo-camera')
  const [chanDoan, setChanDoan] = useState<ChanDoanCamera | null>(null)
  /** Tăng lên để chạy lại effect mở camera — nút "Thử lại". */
  const [lanThu, setLanThu] = useState(0)

  useEffect(() => {
    let huy = false

    const moCamera = async () => {
      // Chẩn đoán TRƯỚC khi gọi: ngữ cảnh không bảo mật là nguyên nhân phổ
      // biến nhất và `getUserMedia` khi đó thậm chí không tồn tại để mà ném
      // lỗi. Trước 11/09/2026 nhánh này đặt trạng thái mà quên đặt `loi`,
      // nên nhân viên nhận một hộp lỗi trống trơn.
      const truoc = kiemTraTruoc()
      if (truoc.ly_do !== 'ok') {
        setChanDoan(truoc)
        setTrangThai(truoc.ly_do === 'khong_bao_mat' ? 'khong-bao-mat' : 'khong-co-camera')
        return
      }

      // Toàn khung xin sẵn khung DỌC 3:4 để lấy được công trình phía sau;
      // chân dung xin khung vuông vì sẽ cắt vuông ngay sau đó.
      const mongMuon: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: 'user' },   // camera trước
          width: { ideal: cheDo === 'toan_khung' ? 1080 : 1280 },
          height: { ideal: cheDo === 'toan_khung' ? 1440 : 1280 },
        },
        audio: false,
      }

      const thu = async (rang_buoc: MediaStreamConstraints) =>
        navigator.mediaDevices.getUserMedia(rang_buoc)

      let stream: MediaStream
      try {
        stream = await thu(mongMuon)
      } catch (e1) {
        if (huy) return
        const cd = chanDoanLoi(e1)
        // Máy không đáp ứng độ phân giải mong muốn: thử lại tối giản thay vì
        // bỏ cuộc. Thà ảnh nhỏ hơn còn hơn không chấm công được (ràng buộc 3).
        if (cd.ly_do === 'khong_dap_ung') {
          try {
            stream = await thu({ video: { facingMode: { ideal: 'user' } }, audio: false })
          } catch (e2) {
            if (huy) return
            setChanDoan(chanDoanLoi(e2))
            setTrangThai('khong-co-camera')
            return
          }
        } else {
          setChanDoan(cd)
          setTrangThai(cd.ly_do === 'chua_cap_quyen' ? 'loi-quyen' : 'khong-co-camera')
          return
        }
      }

      if (huy) { stream.getTracks().forEach((t) => t.stop()); return }
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        try {
          await videoRef.current.play()
        } catch {
          // Một số trình duyệt chặn autoplay ngay cả khi đã `muted`. Luồng
          // camera vẫn chạy, chỉ là khung xem chưa phát — KHÔNG được báo
          // thành lỗi quyền camera, vì nhân viên sẽ đi bật một thứ đã bật.
          // `playsInline` + `muted` khiến trường hợp này hiếm; nếu xảy ra,
          // chạm vào khung hình là phát.
        }
      }
      setTrangThai('san-sang')
    }

    void moCamera()

    return () => {
      huy = true
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    // `cheDo` quyết định tỷ lệ khung xin từ camera → đổi thì phải mở lại
  }, [cheDo, lanThu])

  const chup = useCallback(async () => {
    const video = videoRef.current
    if (!video || video.readyState < 2) return

    setTrangThai('dang-chup')

    const canvas = document.createElement('canvas')
    const toanKhung = cheDo === 'toan_khung'
    // Chân dung: cắt vuông giữa khung, khớp với ô ngắm tròn.
    // Toàn khung: giữ nguyên mọi thứ camera thấy — công trình phía sau chính
    // là bằng chứng, cắt đi là mất.
    const w = toanKhung ? video.videoWidth : Math.min(video.videoWidth, video.videoHeight)
    const h = toanKhung ? video.videoHeight : w
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) { setTrangThai('san-sang'); return }

    ctx.drawImage(
      video,
      (video.videoWidth - w) / 2, (video.videoHeight - h) / 2, w, h,
      0, 0, w, h,
    )

    canvas.toBlob(async (blob) => {
      if (!blob) { setTrangThai('san-sang'); return }
      // Nén + xoá EXIF trước khi rời khỏi máy
      const nen = await nenAnh(blob, CANH_ANH[cheDo], 0.8)
      streamRef.current?.getTracks().forEach((t) => t.stop())
      onXong(nen)
    }, 'image/webp', 0.9)
  }, [onXong, cheDo])

  return (
    <div className="chup-khuon-mat">
      <header className="chup-dau">
        <button type="button" className="nut-dong" onClick={onHuy} aria-label="Đóng">
          ✕
        </button>
        <h2>{tieuDe}</h2>
      </header>

      {trangThai === 'loi-quyen' || trangThai === 'khong-co-camera'
        || trangThai === 'khong-bao-mat' ? (
        <div className="chup-loi" role="alert">
          {/* `thong_diep` không bao giờ rỗng — xem lib/camera.ts. Trước đây
              nhánh "không có mediaDevices" để trống và nhân viên nhìn vào một
              hộp lỗi câm. */}
          <p>{chanDoan?.thong_diep ?? 'Không mở được camera.'}</p>

          {trangThai === 'khong-bao-mat' && (
            <p className="ghi-chu">
              Đây là <strong>yêu cầu của trình duyệt</strong>, không phải lỗi máy
              bạn và không tắt đi được. Camera, định vị và mã offline đều cần
              HTTPS.
            </p>
          )}

          <p className="ghi-chu">
            {onBoQuaAnh
              ? 'Mạng công ty đã xác nhận bạn có mặt, nên vẫn chấm công được — '
                + 'lần này quản lý sẽ xác nhận thêm.'
              : 'Chấm công tại công trình bắt buộc xác minh khuôn mặt. Nếu camera '
                + 'hỏng, báo tổ trưởng để quản lý nhập tay.'}
          </p>

          {onBoQuaAnh && (
            <button type="button" className="nut-thu-lai" onClick={onBoQuaAnh}>
              Vẫn chấm công, chờ quản lý duyệt
            </button>
          )}

          {/* Thử lại có ích thật với "đang bận" và "bị huỷ"; vô ích với HTTPS
              nên không hiện ở đó, tránh cho nhân viên bấm mãi. */}
          {trangThai !== 'khong-bao-mat' && (
            <button type="button" className="nut-thu-lai"
                    onClick={() => {
                      setChanDoan(null)
                      setTrangThai('dang-mo-camera')
                      setLanThu((n) => n + 1)
                    }}>
              Thử mở camera lại
            </button>
          )}

          <button type="button" className="lien-ket-phu" onClick={onHuy}>Quay lại</button>

          {/* Dòng kỹ thuật để nhân viên đọc cho quản trị. Không có nó, mọi báo
              lỗi đều quy về "không chụp được" và không ai lần ra được. */}
          {chanDoan?.chi_tiet && (
            <p className="chi-tiet-ky-thuat">
              Chi tiết cho quản trị: <code>{chanDoan.chi_tiet}</code>
            </p>
          )}
        </div>
      ) : (
        <>
          <div className={`khung-camera ${cheDo === 'toan_khung' ? 'toan-khung' : ''}`}>
            <video
              ref={videoRef}
              playsInline
              muted
              // Lật gương cho giống soi gương, người dùng dễ căn mặt hơn
              style={{ transform: 'scaleX(-1)' }}
            />
            <div className={`o-ngam ${cheDo === 'toan_khung' ? 'o-ngam-tren' : ''}`}
                 aria-hidden="true" />
          </div>

          <p className="huong-dan">{huongDan}</p>

          {duoiKhung}

          <button
            type="button"
            className="nut-chup"
            onClick={() => void chup()}
            disabled={trangThai !== 'san-sang'}
          >
            {trangThai === 'dang-mo-camera' ? 'Đang mở camera…'
              : trangThai === 'dang-chup' ? 'Đang xử lý…'
              : 'Chụp'}
          </button>

          <p className="ghi-chu-rieng-tu">
            Ảnh dùng để xác minh đúng người chấm công, lưu tối đa 24 tháng rồi
            tự xoá. Bạn có quyền rút lại đồng ý bất cứ lúc nào ở mục Cá nhân.
          </p>
        </>
      )}
    </div>
  )
}
